<script setup lang="ts">
/**
 * Where the template shows a row of client logos, we show live counters from
 * our own database. Every figure is rounded down (see formatCount) so it is
 * never higher than reality, and a tile whose counter failed is hidden.
 *
 * The counters are the point of the band, so they get the template's display
 * type, tick up the first time they scroll in, and stand in their own columns
 * with a hairline between them.
 */
const { t } = useI18n()
const { stats, formatCount } = usePublicStats()
const { refresh: refreshReveal } = useScrollReveal()

/** A single-digit counter says "nobody uses this" louder than no tile at all. */
const MIN_SHOWN = 10

const counter = (value: number | null | undefined) => (value && value >= MIN_SHOWN ? formatCount(value) : '')

/**
 * The template's own illustrated icons, the same ones the feature grid uses for
 * these subjects (see constants/featurePages). Their line art is dark, so the
 * square behind them stays light in dark mode as well.
 */
const tiles = computed(() =>
  [
    { id: 'cvs', icon: '/sofax/service-icon-1.svg', label: t('homepage.trust.cvs'), value: counter(stats.value?.cvs) },
    { id: 'users', icon: '/sofax/content-icon-3.svg', label: t('homepage.trust.users'), value: counter(stats.value?.users) },
    { id: 'coverLetters', icon: '/sofax/contact-icon-3.svg', label: t('homepage.trust.coverLetters'), value: counter(stats.value?.coverLetters) },
    { id: 'time', icon: '/sofax/feature-icon-5.svg', label: t('homepage.hero.stats.timeToCreate'), value: t('homepage.hero.stats.timeValue') },
  ].filter(tile => tile.value),
)

/** The counters arrive after hydration, so the tiles that appear then still need revealing. */
watch(tiles, () => refreshReveal())
</script>

<template>
  <section class="sofax relative overflow-hidden border-y border-line bg-white dark:border-gray-800 dark:bg-gray-950">
    <div class="py-14 lg:py-20">
      <div class="shell relative">
        <!-- Template doodles, wide screens only: they frame the figures without crowding them -->
        <img
          src="/sofax/content-shape-1.svg"
          alt=""
          aria-hidden="true"
          width="82"
          height="76"
          loading="lazy"
          class="pointer-events-none absolute right-2 top-0 hidden w-12 select-none lg:block xl:w-16"
        >
        <img
          src="/sofax/background-shape-2.svg"
          alt=""
          aria-hidden="true"
          width="174"
          height="171"
          loading="lazy"
          class="up-down-40 pointer-events-none absolute left-0 top-2 hidden w-16 select-none opacity-70 xl:block"
        >

        <div class="mx-auto mb-12 max-w-2xl text-center md:mb-16" data-reveal>
          <p class="eyebrow !mb-5">
            <span class="relative flex size-2" aria-hidden="true">
              <span class="absolute inline-flex size-full rounded-full bg-accent opacity-75 motion-safe:animate-ping" />
              <span class="relative inline-flex size-2 rounded-full bg-accent" />
            </span>
            {{ t('homepage.trust.eyebrow') }}
          </p>

          <h2 class="!text-3xl md:!text-4xl">{{ t('homepage.trust.heading') }}</h2>
          <p class="mt-4 text-base">{{ t('homepage.trust.title') }}</p>
        </div>

        <!--
          `dt` before `dd` in the markup, reversed for the eye: the label is the
          name of the figure, and the column rule needs the columns to touch, so
          the breathing room is padding rather than a gap.
        -->
        <dl
          class="grid gap-y-10 text-center md:divide-x md:divide-line dark:md:divide-gray-800"
          :class="tiles.length === 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'"
        >
          <div
            v-for="(tile, index) in tiles"
            :key="tile.id"
            class="flex flex-col-reverse items-center justify-end px-2 sm:px-4"
            data-reveal
            :style="{ '--reveal-delay': `${index * 110}ms` }"
          >
            <dt class="mt-3 text-sm text-muted dark:text-gray-400">{{ tile.label }}</dt>
            <dd class="font-title text-[1.75rem] font-bold leading-none -tracking-[1px] text-ink xs:text-4xl sm:text-5xl md:text-6xl dark:text-white">
              <span class="mx-auto mb-4 flex size-14 items-center justify-center rounded-[0.625rem] bg-surface sm:mb-5 sm:size-16">
                <img :src="tile.icon" alt="" aria-hidden="true" width="80" height="80" loading="lazy" class="size-8 sm:size-10">
              </span>
              <SofaxCountUp :value="tile.value" />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </section>
</template>
